import { useTranslation } from "@firecms/core";
import { Select, SelectItem } from "@firecms/ui";

import {
    cmsLocales,
    localeLabels,
    type CmsLocale,
} from "../i18n/locales";

/**
 * Language picker for the CMS chrome, mounted in the app bar.
 *
 * FireCMS ships no switcher of its own — `FireCMSi18nProvider` takes a `locale`
 * prop and otherwise reads `localStorage`, with nothing in the UI to write it.
 * This is that missing control.
 *
 * **Changing the language goes through `i18n.changeLanguage`, not React state.**
 * The provider builds its i18next instance once, on first mount, and ignores
 * later changes to the `locale` prop whenever a stored preference exists — so
 * driving this from a `useState` in `App.tsx` would move the prop and change
 * nothing. `changeLanguage` re-renders every translated string through
 * react-i18next and the provider's own `languageChanged` listener writes the
 * choice to `localStorage`, so persistence is free and per-editor.
 *
 * Only the chrome moves. Collection and field labels are plain strings in
 * `src/collections/*` and stay as authored — see the note in `i18n/zh-TW.ts`.
 */
export function LocaleSwitcher() {
    const { i18n } = useTranslation();

    // i18next resolves "zh" or "zh-TW-x-foo" to our bundle, but the raw value
    // may not match a Select option exactly, which would render it blank.
    const current = (cmsLocales as readonly string[]).includes(i18n.language)
        ? (i18n.language as CmsLocale)
        : undefined;

    return (
        <Select
            size="small"
            value={current}
            placeholder={i18n.language}
            onValueChange={(locale) => i18n.changeLanguage(locale)}
            renderValue={(locale) =>
                localeLabels[locale as CmsLocale] ?? locale
            }
            aria-label="Language"
        >
            {cmsLocales.map((locale) => (
                <SelectItem key={locale} value={locale}>
                    {localeLabels[locale]}
                </SelectItem>
            ))}
        </Select>
    );
}
